import { ref } from 'vue'
import { HONEYPOT_FIELD_NAME, MIN_FORM_FILL_MS } from '@/constants/registration'

/**
 * Two cheap bot signals that cost a real supplier nothing.
 *
 * 1. A field hidden from people but present in the DOM. Anything that fills it in is
 *    filling in fields it cannot see.
 * 2. How long the form was on screen. A human cannot complete sign-up in three seconds.
 *
 * Both are hints, not verdicts: the values travel to the server, which decides. Refusing
 * the submission in the browser would only teach whoever is scripting it which check
 * they tripped.
 */
export function useHoneypot() {
  const honeypotValue = ref('')
  const startedAt = ref(Date.now())

  /** Milliseconds the form has been open. Sent so the server can apply its own floor. */
  const elapsedMs = () => Date.now() - startedAt.value

  /** Local read of the same signals, for anything that wants to react before submitting. */
  const isLikelyBot = () => honeypotValue.value.length > 0 || elapsedMs() < MIN_FORM_FILL_MS

  /** Call after a rejected submit so a retry is not judged by the first render's clock. */
  const reset = () => {
    honeypotValue.value = ''
    startedAt.value = Date.now()
  }

  /** The shape the register endpoint expects. */
  const payload = () => ({
    honeypot: honeypotValue.value,
    formElapsedMs: elapsedMs()
  })

  return {
    fieldName: HONEYPOT_FIELD_NAME,
    honeypotValue,
    elapsedMs,
    isLikelyBot,
    reset,
    payload
  }
}
